<script setup lang="ts">
import { Button, Input } from '@laam/ui/base'; // Import TanStack components
import { PhMagnifyingGlass, PhX } from '@phosphor-icons/vue';
import { useRoute } from 'vue-router';
import { useRouter } from 'vue-router';
import { FBConversion } from '~/utils/fbConversion.ts';
import { useDebounceFn } from '@vueuse/core';

const route = useRoute();
const router = useRouter();

const { $eventClient } = useNuxtApp();
const { deviceType } = useDeviceType();
const isMobile = computed(() => deviceType.value === 'mobile');

const historyLength = computed(() => {
	if (process.client) {
		return window.history.length;
	}
	return 2; //by default window history has length tow
});

const filters = computed(() => {
	const filters = route.fullPath.split('?')[1] || '';
	if (!filters) return '';
	const params = new URLSearchParams(filters);
	params.delete('search');
	params.delete('node_id');

	return params.toString();
});

provide('filters', filters);

const filterValue = ref(route.query.search as string); // Create a reactive data to store the search query
provide('filterValue', filterValue);

const searchQuery = useDebounceFn(async () => {
	if (filterValue.value === '') {
		await router.replace('/search'); // Navigate to the search page with the search query
	} else {
		FBConversion.apiCall({
			event_name: 'search',
			custom_data: {
				search_string: filterValue.value,
			},
		});
		$eventClient.sendEvent('product-search', {
			query: filterValue.value,
		});
		await router.replace(`/search?search=${filterValue.value}`); // Navigate to the search page with the search query
	}
}, 500);

const handleInputChange = () => {
	searchQuery();
};

const handleSearchCancel = () => {
	navigateBack();
};

const clearSearchQuery = () => {
	filterValue.value = '';
	searchQuery();
	const inputField = document.getElementById('inputField');
	if (inputField) {
		inputField.focus();
	}
};

const navigateBack = () => {
	historyLength.value > 2 ? router.back() : navigateTo('/');
};

// :class="{
// 					'lg:max-w-[45vw]': storeType !== 'OCTANE',
// 				}"

// :data-store-type="storeType"
// 		:data-header-visible="isHeaderVisible"
// 		:data-shipping-config="!!shippingConfig"

// FIXME: fix all the styles in dev server here
</script>
<template>
	<div class="w-full py-md">
		<div
			ref="searchInputRef"
			class="flex flex-col z-[10] gap-md bg-white w-full border-b lg:border-b-0"
		>
			<div class="flex space-between lg:gap-10xl lg:p-0 p-xl">
				<div class="lg:gap-7xl flex flex-row items-center w-full">
					<Input
						id="inputField"
						v-model="filterValue"
						class="text-sm text-gray-500 flex items-center bg-gray-100 max-h-9xl rounded-medium gap-md border-gray-200"
						name="laam-search"
						placeholder="Search collections, categories or products"
						:auto-complete="'off'"
						@input="handleInputChange"
					>
						<template #leftIcon>
							<div>
								<PhMagnifyingGlass size="20" />
							</div>
						</template>

						<template #rightIcon>
							<Button
								v-if="filterValue"
								class="laam-search-clear p-sm border-none bg-transparent hover:bg-transparent"
								:variant="isMobile ? 'secondary' : 'tertiary'"
								:size="'lg'"
								@click.stop="clearSearchQuery"
							>
								<span><PhX class="" /> </span>
							</Button>
						</template>
					</Input>
				</div>
				<Button
					class="px-md py-sm laam-search-cancel border-none bg-transparent hover:bg-transparent"
					:variant="isMobile ? 'secondary' : 'tertiary'"
					:size="isMobile ? 'xl' : 'lg'"
					s
					@click="handleSearchCancel"
				>
					<span>Cancel</span>
				</Button>
			</div>
		</div>
	</div>
</template>

<style scoped>
/* TODO: add styles for marketplace */
.search-header[data-header-visible='true'][data-top-banner='false'] {
	z-index: 20;
	top: 0;
	@media (min-width: 768px) {
		top: var(--octane-topbar-height);
	}
}
.search-header[data-header-visible='true'][data-top-banner='true'] {
	z-index: 20;
	top: var(--laam-shipping-banner-height);
	@media (min-width: 768px) {
		top: var(--octane-topbar-height);
	}
}
.search-header[data-header-visible='false'][data-top-banner='false'] {
	z-index: 20;
	top: calc(0 - var(--octane-topbar-height-mobile));
	@media (min-width: 768px) {
		top: calc(0 - var(--octane-topbar-height));
	}
}
.search-header[data-header-visible='false'][data-top-banner='true'] {
	z-index: 20;
	top: calc(
		0 - var(--octane-topbar-height-mobile) - var(--laam-shipping-banner-height)
	);
	@media (min-width: 768px) {
		top: calc(0 - var(--octane-topbar-height));
	}
}
</style>
